<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="h-screen w-screen overflow-y-auto bg-base-100" @scroll="handleScroll">
      <WaveAnimation :visible="showWave" />
      
      <!-- Job Detail Hero Section -->
      <section class="section relative overflow-hidden" id="slide1">
        <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
        <div class="absolute inset-0 bg-pattern opacity-10"></div>
        
        <!-- Decorative Elements -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-xl animate-float"></div>
        <div class="absolute bottom-20 right-20 w-48 h-48 bg-secondary/10 rounded-full blur-2xl animate-float-slow"></div>
        
        <div class="relative z-10 flex items-center justify-center min-h-screen px-6">
          <div class="text-center max-w-4xl mx-auto">
            <!-- Job Header -->
            <div class="job-header mb-8">
              <div class="job-meta-info mb-4">
                <span class="job-number">{{ jobData.jobNumber }}</span>
                <span class="job-date">{{ jobData.revDate }}</span>
              </div>
              <h1 class="job-title text-5xl md:text-6xl font-bold text-white mb-4">
                {{ jobData.title }}
              </h1>
              <div class="job-details-grid">
                <div class="detail-item">
                  <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="detail-icon" />
                  <span class="detail-label">Location:</span>
                  <span class="detail-value">{{ jobData.location }}</span>
                </div>
                <div v-if="jobData.reportsTo" class="detail-item">
                  <font-awesome-icon :icon="['fas', 'user-tie']" class="detail-icon" />
                  <span class="detail-label">Reports to:</span>
                  <span class="detail-value">{{ jobData.reportsTo }}</span>
                </div>
              </div>
            </div>
            
            <!-- Quick Actions -->
            <div class="quick-actions flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button 
                @click="scrollToSection('slide2')"
                class="action-primary inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary/90 
                       text-white font-semibold rounded-full transition-all duration-300 
                       hover:shadow-lg hover:shadow-primary/30 hover:scale-105"
              >
                <span>Apply Now</span>
                <font-awesome-icon :icon="['fas', 'arrow-right']" class="animate-pulse" />
              </button>
              
              <button 
                @click="scrollToSection('slide2')"
                class="action-secondary inline-flex items-center gap-3 px-8 py-4 
                       border-2 border-white/30 hover:border-white/60 text-white 
                       font-semibold rounded-full transition-all duration-300 
                       hover:bg-white/10 hover:scale-105"
              >
                <span>View Full Description</span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="animate-bounce" />
              </button>
              
              <button 
                @click="goBackToListings"
                class="action-tertiary inline-flex items-center gap-3 px-6 py-3 
                       text-white/80 hover:text-white font-medium transition-all duration-300"
              >
                <font-awesome-icon :icon="['fas', 'arrow-left']" />
                <span>Back to Jobs</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Job Description Section -->
      <section class="section min-h-screen" id="slide2">
        <div class="section-content">
          <div class="job-description-container">
            
            <!-- Job Description Header -->
            <div class="description-header">
              <div class="header-table">
                <div class="table-row">
                  <div class="table-cell label">REPORTS DIRECTLY:</div>
                  <div class="table-cell value">{{ jobData.reportsTo }}</div>
                  <div class="table-cell label">POSITION LOCATION:</div>
                  <div class="table-cell value">{{ jobData.location }}</div>
                </div>
                <div class="table-row">
                  <div class="table-cell label">JD NUMBER:</div>
                  <div class="table-cell value">{{ jobData.jobNumber }}</div>
                  <div class="table-cell label">DATE:</div>
                  <div class="table-cell value">{{ jobData.revDate }}</div>
                </div>
              </div>
              <div class="company-logo">
                <img src="/img/STE Logo.png" alt="STE Tank & Equipment" />
              </div>
            </div>

            <!-- Scope of Role -->
            <div class="job-section">
              <h3 class="section-title">Scope of role</h3>
              <!-- scopeOfRole is one paragraph, or an array of paragraphs -->
              <p
                v-for="paragraph in [].concat(jobData.scopeOfRole)"
                :key="paragraph"
                class="section-content-text"
              >{{ paragraph }}</p>
            </div>

            <!-- Schedule (shift details, for production roles) -->
            <div v-if="jobData.schedule" class="job-section">
              <h3 class="section-title">Schedule</h3>
              <ul class="responsibility-list">
                <li v-for="line in jobData.schedule" :key="line">{{ line }}</li>
              </ul>
            </div>

            <!-- Key Responsibilities -->
            <div class="job-section">
              <h3 class="section-title">Key Responsibilities</h3>
              <!-- Grouped (by topic or by level, e.g. Programmer I / II / III) -->
              <template v-if="jobData.responsibilityGroups">
                <div
                  v-for="group in jobData.responsibilityGroups"
                  :key="group.heading"
                  class="responsibility-group"
                >
                  <h4 class="subsection-title">{{ group.heading }}</h4>
                  <p v-if="group.focus" class="group-focus">Focus: {{ group.focus }}</p>
                  <ul class="responsibility-list">
                    <li v-for="responsibility in group.items" :key="responsibility">
                      {{ responsibility }}
                    </li>
                  </ul>
                </div>
              </template>
              <ul v-else class="responsibility-list">
                <li v-for="responsibility in jobData.keyResponsibilities" :key="responsibility">
                  {{ responsibility }}
                </li>
              </ul>
            </div>

            <!-- Professional Experience/Qualifications -->
            <div v-if="jobData.minimumRequirements || jobData.skills" class="job-section">
              <h3 class="section-title">PROFESSIONAL EXPERIENCE/QUALIFICATIONS</h3>

              <div v-if="jobData.minimumRequirements" class="qualifications-subsection">
                <h4 class="subsection-title">Minimum Requirements</h4>
                <ul class="qualification-list">
                  <!-- A requirement is a string, or { text, subItems } for a nested list -->
                  <li
                    v-for="requirement in jobData.minimumRequirements"
                    :key="requirement.text || requirement"
                  >
                    {{ requirement.text || requirement }}
                    <ul v-if="requirement.subItems" class="qualification-list sub-list">
                      <li v-for="subItem in requirement.subItems" :key="subItem">
                        {{ subItem }}
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div v-if="jobData.skills" class="qualifications-subsection">
                <h4 class="subsection-title">Interpersonal, Leadership, and Technical Skills</h4>
                <ul class="qualification-list">
                  <li v-for="skill in jobData.skills" :key="skill">
                    {{ skill }}
                  </li>
                </ul>
              </div>

            </div>

            <!-- Physical Demands -->
            <div v-if="jobData.physicalDemands" class="job-section">
              <h3 class="section-title">Physical Demands</h3>
              <p
                v-for="paragraph in jobData.physicalDemands"
                :key="paragraph"
                class="section-content-text"
              >{{ paragraph }}</p>
            </div>

            <!-- Safety -->
            <div v-if="jobData.safety" class="job-section">
              <h3 class="section-title">Safety</h3>
              <p class="section-content-text">{{ jobData.safety }}</p>
            </div>

            <!-- Apply Now CTA -->
            <div class="apply-cta-section">
              <div class="cta-card">
                <h3>Ready to Join Our Team?</h3>
                <p>Submit your application today and become part of STE's manufacturing excellence team.</p>
                <button @click="scrollToApplication" class="apply-cta-btn">
                  <font-awesome-icon :icon="['fas', 'file-upload']" class="btn-icon" />
                  Apply for This Position
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>

    <!-- Scroll Navigation -->
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow fixed bottom-[60px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow fixed bottom-[10px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>
<script>
import NavBar from '../components/NavBar.vue';
import WaveAnimation from '../components/WaveAnimation.vue';

export default {
  name: 'JobDetail',
  components: {
    NavBar,
    WaveAnimation
  },
  data() {
    return {
      sections: [
        { name: 'JOB OVERVIEW', id: 'slide1' },
        { name: 'FULL DESCRIPTION', id: 'slide2' }
      ],
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      showWave: false,
      jobsData: {
        'JD00021': {
          jobNumber: 'JD00021',
          revision: 'A',
          revDate: '08/09/2023',
          title: 'TECHNICAL SPECIFICATIONS WRITER',
          reportsTo: 'Design Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'The Technical Specifications Writer will contribute to the engineering process by developing the required deliverables, such as specification documents, reviewed supplier\'s documentation, functional descriptions, schematic drawings, and compare quotes with BOMs and Global Shop input.',
          keyResponsibilities: [
            'Compares quotes, BOMs, and engineering designs for accuracy, making corrections as needed',
            'Performs technical evaluations of quotations from suppliers in close cooperation with the purchasing team',
            'Ensures full compliance to all applicable requirements imposed by customer and statutory bodies',
            'Reviews supplier engineering information and documentation; (i.e., mechanical drawings, schematics, calculations, electrical diagrams, etc.)',
            'Contributes to the engineering process by helping to develop the required deliverables, such as specification documents, reviewed supplier\'s documentation, functional descriptions, schematic drawings, and test and validation plans',
            'Helps to prepare cost estimates by studying blueprints, plans, and related customer documents, consulting with engineers, and other professional and technical personnel',
            'Provides input and updates for the engineering database and Global Shop; (i.e., purchase orders, parts, quotes, BOMs, and other documentation)',
            'Performs required technical calculations to validate the design',
            'Participates in design checks and reviews in order to guarantee the required quality',
            'Contributes to the further development of the engineering department',
            'Assists with developing BOMs and routing for projects',
            'Problem solves and communicates effectively with work team',
            'Contributes ideas for continuous improvement of workflow and processes',
            'Follows all safety guidelines and procedures',
            'Performs other duties as assigned'
          ],
          minimumRequirements: [
            'Bachelor\'s degree in business administration, sales, or engineering desired; or equivalent work experience',
            'Minimum 1 – 2 years of administrative sales experience in a manufacturing setting preferred',
            'Experience in pricing and quoting',
            'Quick and accurate data entry skills a must',
            'Working knowledge of Microsoft Office Suite of products including Excel, Word, Outlook, PowerPoint, etc.'
          ],
          skills: [
            'Highly capable multi-tasker',
            'Track record of providing high levels of customer service',
            'Works well under pressure',
            'Strong organizational skills',
            'Able to interact with a diverse work group',
            'Problem Solver',
            'Excellent Communication Skills'
          ],
          physicalDemands: [
            'The physical demands described here are representative of those that must be met by an employee to successfully perform the essential functions of this job. Reasonable accommodations may be made to enable individuals with disabilities to perform the essential functions.',
            'While performing the duties of this job, the employee is regularly required to talk or hear. The employee is frequently required to use hands to finger, handle, or feel and reach with hands and arms. The employee is occasionally required to stand, walk, sit, climb, or balance and stoop, knee, crouch, or crawl. The employee must occasionally lift and/or move up to 50 pounds. Specific vision abilities required by this job include close vision.'
          ],
          safety: 'Employees are required to wear all PPE assigned to this work area. Safety glasses, steel toe/non-slip safety shoes/boots and hearing protection are required at all times and for all areas of the production floor. STE is a drug and alcohol-free work environment. Being impaired or under the influence of legal or illegal drugs is strictly prohibited while you are working.'
        },
        'JD00036': {
          jobNumber: 'JD00036',
          revision: 'B',
          revDate: '09/11/2026',
          title: 'DESIGN ENGINEER III (SENIOR)',
          reportsTo: 'Design Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'The Design Engineer is responsible for the design, development, validation, and continuous improvement of liquid tank trailer products and related systems. This role supports new product development, product customization, manufacturing efficiency, regulatory compliance, cost reduction initiatives, and technical support throughout the product lifecycle. Design Engineers collaborate closely with Manufacturing Engineering, Production, Sales, Purchasing, Quality, and Service teams to ensure products meet customer requirements, industry standards, and company objectives for safety, quality, reliability, and profitability',
          responsibilityGroups: [
            {
              heading: 'Design Engineer III (Senior)',
              focus: 'Technical leadership, and complex problem-solving',
              items: [
                'Lead engineering activities associated with new and repeat tank trailer orders',
                'Lead development of new trailer platforms and major product enhancements',
                'Serve as the technical lead for complex customer and regulatory projects',
                'Develop engineering drawings, specifications, and BOMs',
                'Perform advanced structural, durability, and performance analyses',
                'Develop engineering standards, design guidelines, and best practices',
                'Evaluate emerging technologies, materials, and manufacturing methods',
                'Lead root cause investigations involving product failures and warranty concerns',
                'Drive design-for-manufacturing (DFM) and design-for-assembly (DFA) initiatives',
                'Review and approve engineering drawings and technical documentation',
                'Mentor and develop junior engineering staff',
                'Support strategic planning and long-term product roadmaps',
                'Collaborate with Sales and customers on advanced applications and custom solutions'
              ]
            }
          ],
          minimumRequirements: [
            'Bachelor\'s degree in Mechanical Engineering, Mechanical Engineering Technology, or a closely related engineering discipline',
            'Equivalent combination of education and relevant engineering experience may be considered',
            'Experience designing or engineering tank trailers, pressure vessels, process equipment, piping systems, truck/trailer equipment, or other highly engineered fabricated products',
            'Ability to take engineering projects from requirements through detailed design and manufacturing',
            'Experience interpreting engineering drawings, specifications, and applicable codes and standards',
            'Strong mechanical engineering fundamentals',
            'Proficiency with 3D CAD and 2D engineering drawing systems',
            'Ability to complete FE analysis to verify designs',
            'Familiarity with GD&T, tolerance stack-ups, materials, fabrication, welding, and manufacturing processes',
            'Ability to perform and document engineering calculations',
            'Ability to interpret and apply engineering codes and standards',
            'Experience with stainless steel fabrication, piping, welding, forming, and/or pressure-containing equipment is highly desirable',
            'Working knowledge of engineering documentation, BOMs, ECO/ECN processes, and configuration control',
            'Ability to work effectively with manufacturing and operations personnel',
            'Demonstrated ability to identify problems and develop solutions rather than simply identify constraints'
          ],
          skills: [
            'Ability to manage multiple tasks',
            'Ability to read engineering drawings, technical specifications, design standards, and regulations',
            'Ability to use all extremities to efficiently operate manual tools and power tools',
            'Ability to work in a variety of environments which may include exposure to extreme heat, humidity, fumes, or airborne particles, toxic or caustic chemicals, loud noise, and all-weather conditions',
            'Capable of reading basic measuring equipment i.e., tape measures, rulers, levels etc.',
            'Critical thinking, logic, and reasoning to troubleshoot and solve problems',
            'Efficient computer skills and proficiency in Word, Excel, and Outlook',
            'Effective organizational skills with the ability to work well under pressure',
            'Willingness to work with high energy and a sense of urgency, self-motivated/self-starter',
            'Excellent written and verbal communication skills',
            'Reading comprehension, knowledge of mathematics and use of active listening skills',
            'Strong mathematical and mechanical aptitude with a commitment to highly accurate work',
            'Team player with the ability to lead and work efficiently without supervision',
            'Time management techniques to perform efficiently'
          ],
          physicalDemands: [
            'The physical demands described here are representative of those that must be met by an employee to successfully perform the essential functions of this job. Reasonable accommodations may be made to enable individuals with disabilities to perform the essential functions.',
            'While performing the duties of this job, the employee is regularly required to talk or hear. The employee is frequently required to use hands to finger, handle, or feel and reach with hands and arms. The employee is occasionally required to stand, walk, sit, climb, or balance and stoop, knee, crouch, or crawl. The employee must occasionally lift and/or move up to 50 pounds. Specific vision abilities required by this job include close vision.'
          ],
          safety: 'Employees are required to wear all PPE assigned to this work area. Safety glasses, steel toe/non-slip safety shoes/boots and hearing protection are always required and for all areas of the production floor. STE is a drug and alcohol-free work environment. Being impaired or under the influence of legal or illegal drugs is strictly prohibited while you are working.'
        },
        'JD00067': {
          jobNumber: 'JD00067',
          revision: 'D',
          revDate: '01/30/2026',
          title: 'ROBOTIC WELDING PROGRAMMER',
          reportsTo: 'Manufacturing Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'The Robotic Welding Programmer is responsible for creating and maintaining robotic welding programs. Their duties include program creation/support/improvement, part selection and development, design improvement recommendations, and robotic/automation operations training. The requirements listed below are representative of the knowledge, skill, competency and/or ability required to fulfill these duties. Other duties as assigned.',
          responsibilityGroups: [
            {
              heading: 'Robotic Welding Programmer I',
              focus: 'Execution under guidance, Support, and learning',
              items: [
                'Creates and edits basic robotic & automation programs using established standards.',
                'Reads and interprets drawings and work instructions to support program creation.',
                'Assists with tooling and fixture setup and basic testing.',
                'Makes minor mechanical or program adjustments under guidance.',
                'Verifies engineering information and flags discrepancies to manager.',
                'Notifies manager of quality concerns, delays, or equipment issues.',
                'Collaborates with team members to support daily production needs.',
                'Follows all safety guidelines and standard operating procedures.',
                'Remains flexible and shifts tasks based on manufacturing needs.',
                'Works within assigned deadlines to support production timelines.',
                'Participates in continuous improvement discussions.',
                'Performs other duties as assigned.'
              ]
            },
            {
              heading: 'Robotic Welding Programmer II',
              focus: 'Ownership of processes and independent problem-solving',
              items: [
                'Independently creates, maintains, and improves robotic & automation programs.',
                'Analyzes drawings, work instructions, and engineering data to optimize programs.',
                'Designs, tests, and implements tooling and fixtures to support production and quality.',
                'Makes mechanical and program adjustments to meet quantity and quality requirements.',
                'Collaborates with maintenance to ensure proper and timely equipment upkeep.',
                'Identifies process issues and implements corrective actions.',
                'Suggests design improvements or alternative automation methods.',
                'Guides part selection and supports transition from manual to robotic operations.',
                'Communicates effectively with cross-functional teams regarding issues and solutions.',
                'Contributes ideas for continuous workflow and process improvement.',
                'Supports training of operators and technicians on robotic cell operation.',
                'Ensures work meets safety, quality, and production standards.'
              ]
            },
            {
              heading: 'Robotic Welding Programmer III',
              focus: 'Strategy, leadership, creation, and complex problem-solving',
              items: [
                'Leads development, optimization, and standardization of robotic & automation programs.',
                'Provides technical direction for complex automation and integration challenges.',
                'Drives design improvements and alternative methods in collaboration with engineering.',
                'Leads tooling and fixture design strategy to improve efficiency, quality, and uptime.',
                'Oversees transition of processes from manual to robotic operation.',
                'Partners closely with maintenance and engineering to improve equipment reliability.',
                'Identifies systemic issues and leads root-cause analysis and long-term corrective actions.',
                'Mentors and trains technicians across the facility in robotic cell operation and best practices.',
                'Reviews and validates engineering information for manufacturability.',
                'Sets priorities and adjusts work to meet changing manufacturing demands.',
                'Champions safety, continuous improvement, and best-in-class automation practices.',
                'Serves as escalation point for production, quality, and automation issues.'
              ]
            }
          ],
          minimumRequirements: [
            'Welding certificate, technical degree, or equivalent',
            'Experience with robotic weld cell operation',
            'Robotic weld programming experience (ABB preferred)',
            'AutoCAD/Inventor/3D CAD experience a plus',
            'Experience with robotic and/or automated equipment programming',
            'Familiarity with PLC programming (ABB Preferred).',
            'Experience with sheet metal fabrication, forming, cut-to-length, and/or press brake operation. AutoCAD/Inventor/3D CAD experience a plus'
          ],
          skills: [
            'Excellent computer skills and proficiency in Word, Excel, Outlook',
            'Ability to manage multiple tasks.',
            'Excellent written and verbal communication skills',
            'Excellent organizational skills',
            'Excellent attendance and dependability',
            'Reading comprehension, knowledge of mathematics and use of active listening skills',
            'Critical thinking, logic, and reasoning to troubleshoot and solve problems.',
            'Time management techniques to perform job efficiently.',
            'Mechanical ability, working without supervision and commitment to highly accurate work.'
          ],
          physicalDemands: [
            'The physical demands described here are representative of those that must be met by an employee to successfully perform the essential functions of this job. Reasonable accommodations may be made to enable individuals with disabilities to perform the essential functions.',
            'While performing the duties of this job, the employee is regularly required to talk or hear. The employee is frequently required to use hands to finger, handle, or feel and reach with hands and arms. The employee is occasionally required to stand, walk, sit, climb, or balance and stoop, knee, crouch, or crawl. The employee must occasionally lift and/or move up to 50 pounds. Specific vision abilities required by this job include close vision.'
          ],
          safety: 'Employees are required to wear all PPE assigned to this work area. Safety glasses, steel toe/non-slip safety shoes/boots and hearing protection are required at all times and for all areas of the production floor. STE is a drug and alcohol-free work environment. Being impaired or under the influence of legal or illegal drugs is strictly prohibited while you are working.'
        },
        'JD00077': {
          jobNumber: 'JD00077',
          revision: 'B',
          revDate: '05/12/2026',
          title: 'WELDING ENGINEER',
          reportsTo: 'Engineering Director',
          location: 'STE; on site',
          scopeOfRole: 'The Weld Engineer is responsible for developing welding techniques, procedures, and equipment applications to assist with the fabrication of metals.',
          keyResponsibilities: [
            'Supports and troubleshoots welding processes',
            'Investigates and documents welding defects to determine the appropriate cause and potential corrective measures',
            'Develops welding procedures that effectively utilize new welding technology',
            'Designs welding projects and communicates specific equipment and component requirements',
            'Troubleshoots welding problems and provides guidance for product material selection',
            'Improves production capabilities, optimizes welding processes, and drives weld improvements',
            'Evaluates and improves workflow',
            'Creates and updates manufacturing work instructions, equipment manuals, and technical specifications',
            'Evaluates production processes and makes recommendations for improvements',
            'Analyzes production processes, schedules, methods, and other data and then provides management with reports containing the data and statistics to enable management to better understand future requirements needed for the manufacturing process',
            'Ensures that the company\'s manufacturing processes are in compliance with all equipment requirements and limitations',
            'Provides flexible program offerings across multiple machines',
            'Ensures traceability and repeatability through programs and ERP communications',
            'Troubleshoots program challenges and reduce product inconsistencies',
            'Develops fixtures for program applications',
            'Manages fixture assets and instructions for successful and efficient fixture selection and use',
            'Problem solves and communicates effectively with work team',
            'Contributes ideas for continuous improvement of workflow and processes',
            'Follows all safety guidelines and procedures',
            'Performs other duties as assigned'
          ],
          minimumRequirements: [
            'Bachelor\'s degree in weld engineering, manufacturing engineering, systems engineering, industrial engineering, or mechanical engineering',
            'Welding experience with sheet metal, weldments, and large assemblies',
            'Experience with ERP systems, Use computer-aided design (CAD) software to create drawings for welding projects',
            'Strong understanding of manufacturing processes',
            'ISO 9000 experience',
            'CWI D1.2 and/or D1.6 endorsement preferred',
            'Proven track record of developing and qualifying welding procedures for stainless steel and aluminum across structural, over-the-road, agricultural, and pressure-containing applications',
            'Experience in advanced welding process development, specifically cold wire GTAW, hot wire GTAW, hybrid laser welding, and laser hot wire / laser cold wire systems',
            {
              text: 'AWS Certified Welding Engineer (CWEng) certification. Strong working knowledge and hands-on experience with the following codes and standards:',
              subItems: [
                'AWS D1.2/D1.2M – Structural Welding Code – Aluminum',
                'AWS D1.6/D1.6M – Structural Welding Code – Stainless Steel',
                'AWS D14.3/D14.3M – Specification for Welding Earthmoving, Construction, and Agricultural Equipment',
                'ASME BPVC Section IX – Welding, Brazing, and Fusing Qualifications (with practical experience qualifying WPS/PQR for pressure vessels)',
                'Demonstrated experience with ASME Section VIII pressure vessel'
              ]
            }
          ],
          skills: [
            'Analytical skills to properly interpret data',
            'Ability to explain technical concepts in simple terms',
            'Ability to lead and teach a team',
            'Ability to work well under pressure',
            'Commercial awareness',
            'Critical thinking and problem-solving skills',
            'Excellent organizational skills',
            'Go-to GEMBA and 6S bias',
            'Good communication and interpersonal skills',
            'Good influencing skills',
            'Good information technology skills',
            'Hands-on and detail-oriented but also able to see the big picture',
            'High energy and able to handle conflict',
            'Knowledge of equipment for purchase, installation, and repair',
            'Leadership skills',
            'Problem-solving skills',
            'Sense of urgency'
          ],
          physicalDemands: [
            'The physical demands described here are representative of those that must be met by an employee to successfully perform the essential functions of this job. Reasonable accommodations may be made to enable individuals with disabilities to perform the essential functions.',
            'While performing the duties of this job, the employee is regularly required to talk or hear. The employee is frequently required to use hands to finger, handle, or feel and reach with hands and arms. The employee is occasionally required to stand, walk, sit, climb, or balance and stoop, knee, crouch, or crawl. The employee must occasionally lift and/or move up to 50 pounds. Specific vision abilities required by this job include close vision.'
          ],
          safety: 'Employees are required to wear all PPE assigned to this work area. Safety glasses, steel toe/non-slip safety shoes/boots and hearing protection are required at all times and for all areas of the production floor. STE is a drug and alcohol-free work environment. Being impaired or under the influence of legal or illegal drugs is strictly prohibited while you are working.'
        },
        'JD00078': {
          jobNumber: 'JD00078',
          revision: 'A',
          revDate: '01/28/2025',
          title: 'QUALITY ENGINEER',
          reportsTo: 'Quality Director',
          location: 'STE; on site',
          scopeOfRole: 'The Quality Engineer is responsible for ensuring compliance of products and services to STE, customer, and regulatory standards. This position is responsible for helping to prevent issues using tools such as PFMEA and control plans, and for recommending solutions for issues that arise. He/she will also be involved with improving and updating the quality management system, working with vendors, and assisting with the warranty system.',
          responsibilityGroups: [
            {
              heading: 'Supplier Quality',
              items: [
                'Works with the Supply Chain Department to select and evaluate suppliers',
                'Determines which suppliers/purchased products require incoming inspection',
                'Determines incoming inspect criteria, frequency, and sample size',
                'Leads supplier containment and corrective actions',
                'Participates in regular review of supplier scorecards to determine necessary actions to take'
              ]
            },
            {
              heading: 'Nonconforming Material and Corrective Actions',
              items: [
                'Works with appropriate departments to determine disposition of nonconforming material',
                'Conducts root cause analysis to resolve quality issues',
                'Develops and implements corrective action plans to address quality issues'
              ]
            },
            {
              heading: 'Warranty',
              items: [
                'Works with subject matter experts as necessary to determine whether customer complaints are covered under STE\'s warranty policy and how to best address',
                'Prepares metrics for regular reviews of warranty data to determine areas of focus for improvement'
              ]
            },
            {
              heading: 'Risk Assessment and Minimization',
              items: [
                'Works with the Engineering Department and others to assess risk using tools such as PFMEA and DFM',
                'Adjusts control plans/inspection plans as needed to account for changes to risk level',
                'Participates in design verification and validation',
                'Participates in qualification of new or revised equipment (measurements and data analysis for FAT, SAT)'
              ]
            },
            {
              heading: 'Measurement Systems',
              items: [
                'Performs measurement system analysis (GR&R, MSA) as needed',
                'Determines/adjusts calibration frequencies as needed',
                'Uses Faro Arm and other gauges to support qualifications, calibrations, and tooling checks'
              ]
            },
            {
              heading: 'Data analysis and Metrics',
              items: [
                'Prepares metrics for review by leadership and process owners',
                'Analyzes data to assist with prioritization of improvement efforts'
              ]
            },
            {
              heading: 'Quality Management System',
              items: [
                'Plans and performs internal ISO audits',
                'Updates documentation as required'
              ]
            },
            {
              heading: 'Continuous Improvement',
              items: [
                'Contributes ideas for continuous improvement of workflow and processes',
                'Tracks continuous improvement projects and progress against them',
                'Participates in improvement teams to apply Lean, Poke Yoke, and other tools'
              ]
            },
            {
              heading: 'Additional/Other',
              items: [
                'Ensures that processes adhere to quality standards through control plans/inspection plans',
                'Follows all safety guidelines and procedures',
                'Performs other duties as assigned'
              ]
            }
          ],
          minimumRequirements: [
            'Bachelor\'s degree in engineering, ASQ certified Quality Engineer or equivalent training and experience',
            '2-3 years of experience as a Quality or Process/Manufacturing Engineer',
            'Knowledge of AIAG PPAP requirements',
            'Education and experience in statistical methods, including SPC and gauge R&R',
            'ISO 9000 experience, in particular Lead Auditor qualifications',
            'Knowledge and experience in DFM (Design for Manufacture) a plus',
            'Experience working with vendors, including audits, scoring, and corrective actions.'
          ],
          skills: [
            'Analytical skills to properly interpret data',
            'Passionate and customer-focused',
            'Hands-on and detail-oriented but also able to see the big picture 80/20',
            'Good communication and interpersonal skills',
            'Critical thinking and problem-solving skills',
            'Ability to explain technical concepts in simple terms',
            'Excellent organizational skills and ability to effectively prioritize multiple tasks',
            'Good influencing skills',
            'Ability to work well under pressure',
            'Sense of urgency',
            'Good information technology skills'
          ],
          physicalDemands: [
            'The physical demands described here are representative of those that must be met by an employee to successfully perform the essential functions of this job. Reasonable accommodations may be made to enable individuals with disabilities to perform the essential functions.',
            'While performing the duties of this job, the employee is regularly required to talk or hear. The employee is frequently required to use hands to finger, handle, or feel and reach with hands and arms. The employee is occasionally required to stand, walk, sit, climb, or balance and stoop, knee, crouch, or crawl. The employee must occasionally lift and/or move up to 50 pounds. Specific vision abilities required by this job include close vision.'
          ],
          safety: 'Employees are required to wear all PPE assigned to this work area. Safety glasses, steel toe/non-slip safety shoes/boots and hearing protection are required at all times and for all areas of the production floor. STE is a drug and alcohol-free work environment. Being impaired or under the influence of legal or illegal drugs is strictly prohibited while you are working.'
        },
        'JD00079': {
          jobNumber: 'JD00079',
          revision: 'A',
          revDate: '01/14/2025',
          title: 'MANUFACTURING TRAINING SPECIALIST',
          reportsTo: 'HR Director',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a dynamic and experienced Training Specialist to design, develop, and deliver effective training programs that enhance organizational performance. This position will develop and deliver training on production processes, safety, and equipment for new and existing employees, focusing on hands-on skill-building, standard operating procedures (SOPs), and compliance, while assessing needs, creating materials, tracking progress, and collaborating with management to boost efficiency and maintain a skilled workforce. Key duties include designing curriculum, facilitating sessions (classroom/on-the-floor), evaluating performance, maintaining records, and ensuring training ties to business goals like quality, cost, and safety.',
          keyResponsibilities: [
            'Collaborate with leadership to identify skill gaps, then design, develop, and update training programs, manuals, and materials (e.g., videos, e-learning) for new hires and ongoing training',
            'Conduct engaging, hands-on training on machinery, safety protocols (like PIT operation), quality standards, and SOPs in both classroom and production floor settings',
            'Provide one-on-one coaching, feedback, and support to employees to enhance performance and ensure proper procedure adherence',
            'Assess employee competency through practical tests, monitor performance post-training, track progress, and report on effectiveness to management',
            'Manage training schedules, attendance records, budget, facility setup, and maintain accurate training documentation',
            'Stay current with new manufacturing techniques and technologies, and partner with operations, quality, and safety teams for ongoing improvement',
            'Develop and deliver training in areas including: Onboarding/New hire, Manufacturing, production, soft skills, safety, and equipment',
            'Create Standard work instructions'
          ],
          minimumRequirements: [
            'Proven experience in instructional design, curriculum development, and adult education principles',
            'Excellent project management skills with the ability to handle multiple initiatives simultaneously',
            'Demonstrated teaching or classroom experience combined with outstanding presentation skills',
            'Exceptional written communication skills for content development and documentation purposes',
            'Effective verbal communication skills for delivering engaging training sessions and facilitating discussions',
            'Ability to work independently as well as collaboratively within cross-functional teams',
            'Bachelor\'s degree in Education, Instructional Design, Human Resources, or related field; advanced certifications are a plus',
            'Bi-Lingual (Spanish) required'
          ]
        },
        'JD00084': {
          jobNumber: 'JD00084',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'TIG WELDER',
          reportsTo: '',
          location: 'STE; on site',
          scopeOfRole: 'Welds and assembles stainless or aluminum components in manufacturing of tank trailers for liquid transportation in the chemical, food and sanitary industries.',
          schedule: [
            '1st shift',
            '5:00am to 3:30pm, Monday through Thursday 10 hour shifts',
            'OT available on Fridays'
          ],
          keyResponsibilities: [
            'TIG Weld stainless or aluminum sheets for the production of tanks in various positions: flat, horizontal, vertical or overhead, etc.',
            'Identify any fault in equipment operation or in relation to any material or equipment and notifying the supervisor',
            'Clamp, hold, tack-weld, heat-bend, bolting, or grind material as necessary to satisfy the technical requirements and create a welded final product',
            'Operate other accessory equipment and perform some tasks necessary for welding metal, such as fusing metal segments, using gas tungsten arc, metal arc, flux-cored arc, plasma arc, and other types of welding',
            'Control the processes of fitting, burning and welding to avoid problems in relation to overheat, distortion, expansion or shrink of parts',
            'Examine finished product to ensure specifications are met',
            'Prepare and set up necessary tools and equipment for welding job such as shielded metal arc, gas metal arc or other welding equipment',
            'Read and understand basic prints/drawings to ensure accuracy of work performed'
          ]
        },
        'JD00090': {
          jobNumber: 'JD00090',
          revision: 'A',
          revDate: '05/23/2026',
          title: 'SR. QUALITY MANAGER',
          reportsTo: 'EVP',
          location: 'STE; on site',
          scopeOfRole: [
            'The Senior Quality Manager is responsible for leading the organization\'s Quality Management System (QMS) and driving enterprise-wide quality performance across manufacturing, suppliers, and product lifecycle processes. This role ensures compliance with ISO standards, customer requirements, and internal specifications while advancing a culture of quality excellence, defect prevention, and continuous improvement.',
            'The position partners cross-functionally with Engineering, Operations, Supply Chain, and Leadership to ensure quality is embedded from design through production and delivery.'
          ],
          responsibilityGroups: [
            {
              heading: 'Leadership & Quality Culture',
              items: [
                'Lead, coach, and develop the quality organization, ensuring alignment with company objectives and performance expectations',
                'Establish a culture of accountability, continuous improvement, and proactive quality ownership across all functions',
                'Drive prioritization of initiatives using 80/20 principles to maximize impact on cost of quality and customer satisfaction'
              ]
            },
            {
              heading: 'Quality Strategy & Standards Deployment',
              items: [
                'Define, standardize, and deploy Critical to Quality (CTQ) specifications across products, processes, and suppliers',
                'Act as the primary liaison to align internal standards with customer and vendor quality requirements',
                'Ensure quality requirements are clearly documented and maintained within Standard Work Instructions (SWIs) and QMS documentation'
              ]
            },
            {
              heading: 'Quality Management System (QMS) & Compliance',
              items: [
                'Maintain and improve the ISO 9000-compliant QMS, including document control, process adherence, and audit readiness',
                'Ensure all processes consistently meet regulatory, customer, and internal quality standards',
                'Safeguard the integrity of the QMS during process changes and continuous improvement initiatives'
              ]
            },
            {
              heading: 'Supplier & Incoming Quality',
              items: [
                'Lead supplier quality strategy, including vendor qualification, performance monitoring, and corrective actions',
                'Implement and manage supplier scorecards, incoming inspection processes, and AQL methodologies',
                'Standardize supplier quality expectations and drive continuous improvement across the supply base'
              ]
            },
            {
              heading: 'Manufacturing Quality & Process Excellence',
              items: [
                'Partner with Engineering and Operations to drive defect prevention, process capability, and manufacturing simplification',
                'Implement poka-yoke and error-proofing methods to eliminate non-conformances',
                'Lead capability studies, validation (including new equipment), and SPC deployment across production processes'
              ]
            },
            {
              heading: 'Problem Solving & Continuous Improvement',
              items: [
                'Lead structured problem-solving efforts using RCCM methodologies and support Engineering Change Notice (ECN) processes',
                'Drive root cause identification and systemic corrective actions to prevent recurrence',
                'Champion lean, Six Sigma, and statistical methods to improve quality performance and reduce waste'
              ]
            },
            {
              heading: 'Inspection, Testing & Validation',
              items: [
                'Oversee execution of incoming, progressive inspection, in-process, and final inspection processes to ensure product conformance',
                'Implement robust testing and validation processes for products, equipment, and manufacturing systems',
                'Ensure compliance with PSO, SAT/FAT, PPAP requirements for customers, suppliers, and new product/equipment introductions'
              ]
            },
            {
              heading: 'Quality Performance & Reporting',
              items: [
                'Define, monitor, and report key quality metrics, including cost of quality, FPY, scrap, warranty, and non-conformance trends',
                'Analyze data to identify risks, drive corrective actions, and achieve KPI targets',
                'Maintain accurate records of non-conformances, scrap, returns, and corrective actions'
              ]
            },
            {
              heading: 'Customer & Warranty Quality',
              items: [
                'Lead response and resolution for customer quality issues and warranty claims',
                'Ensure timely root cause analysis and corrective action implementation to maintain customer satisfaction',
                'Track and reduce field failures through structured feedback loops into design and manufacturing'
              ]
            },
            {
              heading: 'Cross-Functional Collaboration',
              items: [
                'Partner closely with Design, Manufacturing, Supply Chain, and ERP teams to ensure quality integration across all processes',
                'Align quality execution with production schedules, delivery deadlines, and engineering requirements',
                'Support digital integration of quality data within ERP/MES environments (where applicable)'
              ]
            },
            {
              heading: 'Training & Capability Development',
              items: [
                'Train and develop personnel in quality tools, statistical methods, SPC, and QMS requirements',
                'Ensure competency in inspection processes, capability studies, and problem-solving methodologies',
                'Promote knowledge sharing and best practices across departments'
              ]
            },
            {
              heading: 'Compliance, Safety & Governance',
              items: [
                'Ensure adherence to all safety, quality, and regulatory requirements',
                'Maintain audit readiness and support internal/external audits',
                'Uphold company policies related to quality, data integrity, and operational excellence'
              ]
            }
          ],
          minimumRequirements: [
            'Bachelor\'s degree in Manufacturing, Industrial, Mechanical, Systems, or Quality Engineering (or related field)',
            '5–10+ years of progressive experience in quality leadership within manufacturing environments',
            'Demonstrated experience with ISO 9000 / ISO 9001 Quality Management Systems',
            'Strong working knowledge of AIAG standards, PPAP, APQP, SPC, and MSA',
            'Experience with Lean, Six Sigma, 6S, and statistical analysis tools',
            'Familiarity with supplier quality management and vendor development',
            'Experience with highly regulated quality standards',
            'Experience in automated or highly engineered manufacturing environments'
          ],
          skills: [
            'Management skills',
            'Analytical skills to properly interpret data',
            'Passionate and customer-focused',
            'Hands-on and detail-oriented but also able to see the big picture 80/20',
            'High energy and able to handle conflict',
            'Good communication and interpersonal skills',
            'Ability to lead and teach a team',
            'Critical thinking and problem-solving skills',
            'Ability to explain technical concepts in simple terms',
            'Excellent organizational skills',
            'Good influencing skills',
            'Ability to work well under pressure',
            'Leadership skills',
            'Sense of urgency',
            'Problem-solving skills',
            'Good information technology skills',
            'Industry related experience'
          ],
          physicalDemands: [
            'The physical demands described here are representative of those that must be met by an employee to successfully perform the essential functions of this job. Reasonable accommodation may be made to enable individuals with disabilities to perform the essential functions.',
            'While performing the duties of this job, the employee is regularly required to talk or hear. The employee is frequently required to use hands to finger, handle, or feel and reach with hands and arms. The employee is occasionally required to stand, walk, sit, climb, or balance and stoop, knee, crouch, or crawl. The employee must occasionally lift and/or move up to 50 pounds. Specific vision abilities required by this job include close vision.'
          ],
          safety: 'Employees are required to wear all PPE assigned to this work area. Safety glasses, steel toe/non-slip safety shoes/boots and hearing protection are required at all times and for all areas of the production floor. STE is a drug and alcohol-free work environment. Being impaired or under the influence of legal or illegal drugs is strictly prohibited while you are working.'
        }

      }
    };
  },
  computed: {
    jobData() {
      const jobId = this.$route.params.id;
      return this.jobsData[jobId] || this.getDefaultJobData();
    }
  },
  mounted() {
    // Initialize scroll state
    const container = document.querySelector('.overflow-y-auto');
    if (container) {
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    }
  },
  methods: {
    getDefaultJobData() {
      return {
        jobNumber: 'Not Found',
        revision: '',
        revDate: '',
        title: 'Position Not Found',
        reportsTo: '',
        location: '',
        scopeOfRole: 'The requested job position could not be found.',
        keyResponsibilities: [],
        minimumRequirements: []
      };
    },

    handleScroll(event) {
      const container = event.target;
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    
    scrollToSection(id) {
      console.log(`Scrolling to section: ${id}`);
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    
    scrollToNextSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      const nextSection = sections.find(
        (section) => section.offsetTop > currentScroll + tolerance
      );

      if (nextSection) {
        console.log(`Scrolling to next section: ${nextSection.id}`);
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },
    
    scrollToPreviousSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      let previousSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop + tolerance < currentScroll) {
          previousSection = section;
          break;
        }
      }

      if (previousSection) {
        console.log(`Scrolling to previous section: ${previousSection.id}`);
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        this.scrollToTop();
      }
    },
    
    scrollToTop() {
      const container = document.querySelector('.overflow-y-auto');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    },

    goBackToListings() {
      this.$router.push('/careers');
    },

    scrollToApplication() {
      // Navigate back to careers page and scroll to application
      this.$router.push('/careers').then(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            const container = document.querySelector('.overflow-y-auto');
            const element = document.getElementById('slide2');
            if (element && container) {
              container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
            }
          }, 100);
        });
      });
    }
  }
};
</script>

<style scoped>
.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  z-index: 100;
}

.h-screen.overflow-y-auto {
  background: radial-gradient(ellipse at bottom, var(--color-base-100) 0%, var(--color-base-300) 90%);
  background-attachment: fixed;
  position: relative;
  scrollbar-width: none;
  z-index: 0;
  -webkit-overflow-scrolling: touch;
}

.section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
  overflow: hidden;
}

.section-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section Styles */
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Job Header */
.job-header {
  text-align: center;
}

.job-meta-info {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
}

.job-number, .job-date {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  font-weight: 500;
}

.job-title {
  background: linear-gradient(135deg, #ffffff 0%, #b7b9ba 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  line-height: 1.1;
  margin-bottom: 2rem;
}

.job-details-grid {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 3rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  font-weight: 500;
}

.detail-icon {
  color: #a22a21;
  font-size: 1.2rem;
}

.detail-label {
  color: #a22a21;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  color: rgba(255, 255, 255, 0.9);
}

/* Quick Actions */
.quick-actions {
  margin-bottom: 2rem;
}

.action-primary, .action-secondary, .action-tertiary {
  min-width: 200px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-primary:hover, .action-secondary:hover, .action-tertiary:hover {
  text-decoration: none;
  color: white;
}

/* Job Description Container */
.job-description-container {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 20px;
  padding: 3rem;
  margin: 2rem 0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(162, 42, 33, 0.1);
}

/* Description Header Table */
.description-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 3px solid #a22a21;
}

.header-table {
  flex: 1;
}

.table-row {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 1rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.table-row:first-child {
  grid-template-columns: auto 1fr auto 1fr;
}

.table-cell.label {
  font-weight: bold;
  color: #2d3748;
  font-size: 0.9rem;
  white-space: nowrap;
}

.table-cell.value {
  color: #4a5568;
  font-size: 0.9rem;
}

.company-logo {
  flex-shrink: 0;
  margin-left: 2rem;
}

.company-logo img {
  height: 60px;
  width: auto;
}

/* Job Sections */
.job-section {
  margin-bottom: 3rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #a22a21;
  padding-bottom: 0.5rem;
}

.section-content-text {
  color: #4a5568;
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 1rem;
}

/* Lists */
.responsibility-list, .qualification-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.responsibility-list li, .qualification-list li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.75rem;
  color: #4a5568;
  line-height: 1.6;
  text-align: left;
}

.responsibility-list li::before, .qualification-list li::before {
  content: '•';
  position: absolute;
  left: 0.5rem;
  color: #a22a21;
  font-weight: bold;
  font-size: 1.2rem;
}

/* Qualifications Subsections */
.qualifications-subsection {
  margin-bottom: 2.5rem;
}

.subsection-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1rem;
  text-decoration: underline;
  text-decoration-color: #a22a21;
}

/* Grouped responsibilities (by topic or by level) */
.responsibility-group {
  margin-bottom: 2rem;
}

.group-focus {
  color: #4a5568;
  font-style: italic;
  margin: -0.5rem 0 1rem;
}

/* Nested requirement bullets (e.g. a list of codes under one requirement) */
.qualification-list.sub-list {
  margin-top: 0.75rem;
}

.qualification-list.sub-list li::before {
  content: '◦';
}


/* Apply CTA Section */
.apply-cta-section {
  margin-top: 4rem;
  text-align: center;
}

.cta-card {
  background: linear-gradient(135deg, rgba(162, 42, 33, 0.1), rgba(183, 185, 186, 0.05));
  border: 2px solid rgba(162, 42, 33, 0.2);
  border-radius: 20px;
  padding: 3rem;
  backdrop-filter: blur(10px);
}

.cta-card h3 {
  font-size: 2rem;
  color: #2d3748;
  margin-bottom: 1rem;
  font-weight: 700;
}

.cta-card p {
  color: #4a5568;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.apply-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #a22a21, #8f0000);
  color: white;
  padding: 1.25rem 2.5rem;
  border: none;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
}

.apply-cta-btn:hover {
  background: linear-gradient(135deg, #8f0000, #7d0000);
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(162, 42, 33, 0.4);
}

/* Animations */
@keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(2deg); 
  }
}

@keyframes float-slow {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-30px) rotate(-3deg); 
  }
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 12s ease-in-out infinite;
}

/* Fade Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .job-title {
    font-size: 2.5rem;
  }
  
  .job-details-grid {
    flex-direction: column;
    gap: 1rem;
  }
  
  .quick-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .action-primary, .action-secondary {
    width: 100%;
    max-width: 300px;
  }
  
  .job-description-container {
    padding: 2rem;
    margin: 1rem 0;
  }
  
  .description-header {
    flex-direction: column;
    gap: 2rem;
  }
  
  .company-logo {
    margin-left: 0;
    text-align: center;
  }
  
  .table-row {
    grid-template-columns: auto 1fr;
    gap: 0.5rem;
  }
  
  .table-row:nth-child(2) {
    grid-template-columns: auto 1fr auto 1fr;
  }
  
  .cta-card {
    padding: 2rem;
  }
  
  .cta-card h3 {
    font-size: 1.5rem;
  }
  
  .job-meta-info {
    flex-direction: column;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .job-title {
    font-size: 2rem;
  }
  
  .job-description-container {
    padding: 1.5rem;
  }
  
  .section-title {
    font-size: 1.25rem;
  }
  
  .table-row {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
  
  .table-cell.label {
    font-weight: bold;
    margin-bottom: 0.25rem;
  }
  
  .table-cell.value {
    margin-bottom: 0.75rem;
    padding-left: 1rem;
  }
  
  .cta-card {
    padding: 1.5rem;
  }
}
</style>
